import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { hqApi } from "@/hq/hqApi";
import { Card, EmptyState, Skeleton, StageBadge, PriorityBadge, Pagination, SearchInput } from "@/hq/HQPrimitives";

const STAGES = ["new","contacted","qualified","discovery","quoted","won","lost"];

export default function HQLeads() {
  const [q, setQ] = useState("");
  const [stage, setStage] = useState("");
  const [sort, setSort] = useState("newest");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    setData(null);
    const params = { page, page_size: 25, sort };
    if (q) params.q = q;
    if (stage) params.stage = stage;
    hqApi.get("/leads", { params })
      .then((r) => alive && setData(r.data))
      .catch(() => alive && setErr("Something went wrong loading leads."));
    return () => { alive = false; };
  }, [q, stage, sort, page]);

  const items = data?.items || [];

  return (
    <div className="space-y-4" data-testid="hq-leads">
      <Card className="p-4 md:p-5">
        <div className="flex flex-wrap items-center gap-3">
          <SearchInput value={q} onChange={(v) => { setQ(v); setPage(1); }} placeholder="Search business name…" testId="hq-leads-search" />
          <select
            value={stage}
            onChange={(e) => { setStage(e.target.value); setPage(1); }}
            data-testid="hq-leads-stage-filter"
            className="px-3 py-2.5 rounded-full border border-[#EAEAEA] bg-white text-[13px] focus:outline-none focus:ring-2 focus:ring-[#FF6A00]"
          >
            <option value="">All stages</option>
            {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            data-testid="hq-leads-sort"
            className="px-3 py-2.5 rounded-full border border-[#EAEAEA] bg-white text-[13px]"
          >
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
          </select>
          <div className="ml-auto text-[11px] tracking-[0.24em] uppercase text-[#8A8A8A]" data-testid="hq-leads-total">
            {data ? `${data.total} lead${data.total === 1 ? "" : "s"}` : "…"}
          </div>
        </div>
      </Card>

      <Card className="p-0 overflow-hidden">
        {!data ? (
          <div className="p-6 space-y-3">
            {[0,1,2].map(i => <Skeleton key={i} h={40} />)}
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            title="No leads yet"
            hint="New OrangeBySite enquiries will appear here as leads."
            testId="hq-leads-empty"
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[14px]">
              <thead className="bg-[#F6F5F2] text-[10px] tracking-[0.22em] uppercase text-[#8A8A8A]">
                <tr>
                  <th className="text-left px-5 py-3 font-bold">Business</th>
                  <th className="text-left px-5 py-3 font-bold">Contact</th>
                  <th className="text-left px-5 py-3 font-bold">Service</th>
                  <th className="text-left px-5 py-3 font-bold">Stage</th>
                  <th className="text-left px-5 py-3 font-bold">Priority</th>
                  <th className="text-left px-5 py-3 font-bold">Source</th>
                  <th className="text-left px-5 py-3 font-bold">Created</th>
                  <th className="text-right px-5 py-3 font-bold w-1"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((l) => (
                  <tr key={l.id} className="border-t border-[#EAEAEA] hover:bg-[#FFFDF6]" data-testid={`hq-lead-row-${l.id}`}>
                    <td className="px-5 py-3">
                      <Link to={`/hq/leads/${l.id}`} className="font-bold text-[#0A0A0A] hover:text-[#FF6A00]">
                        {l.business?.name || "—"}
                      </Link>
                    </td>
                    <td className="px-5 py-3 text-[#4A4A4A]">
                      {l.contact?.full_name || l.contact?.email || "—"}
                    </td>
                    <td className="px-5 py-3 text-[#4A4A4A]">{l.service?.name || "—"}</td>
                    <td className="px-5 py-3"><StageBadge value={l.stage} /></td>
                    <td className="px-5 py-3"><PriorityBadge value={l.priority} /></td>
                    <td className="px-5 py-3 text-[12px] tracking-[0.14em] uppercase text-[#8A8A8A]">{l.source || "—"}</td>
                    <td className="px-5 py-3 text-[12px] text-[#8A8A8A]">
                      {l.created_at ? new Date(l.created_at).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-5 py-3 text-right whitespace-nowrap">
                      {l.business?.id ? (
                        <Link
                          to={`/hq/businesses/${l.business.id}`}
                          data-testid={`hq-lead-open-client-${l.id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[10px] font-black tracking-[0.16em] uppercase bg-white border border-[#EAEAEA] hover:border-[#FF6A00] hover:text-[#FF6A00] transition-colors"
                        >
                          Open Client
                        </Link>
                      ) : (
                        <span
                          data-testid={`hq-lead-open-client-disabled-${l.id}`}
                          title="No linked client"
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[10px] font-black tracking-[0.16em] uppercase bg-white border border-[#EAEAEA] text-[#C9C9C9] cursor-not-allowed"
                        >
                          Open Client
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {data && <Pagination page={data.page} total_pages={data.total_pages} onPage={setPage} testIdPrefix="hq-leads-page" />}
    </div>
  );
}
